import { useEffect, useState } from 'react'

import { UseFormMethods } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { HiOutlineOfficeBuilding } from 'react-icons/hi'

import { Box, FormErrorMessage, FormLabel, HStack } from '@chakra-ui/react'

import {
  AddressSearch,
  FormInputControl,
  FormInputSelectControl,
  PinMarker,
  StaticMapBox,
} from '@/components/ui'
import { StyledFormInputControl } from '@/components/ui'
import { FacilityType } from '@/graphql/generated/schemas'

import {
  FormInputsIProps,
  LabelValueIProps,
  LatLngStateIProps,
} from '../types/types'
import { latRegex, lonRegex } from '../utils/utils'

interface CreateFacilityFormIProps {
  form: UseFormMethods<FormInputsIProps>
  facilityTypes: LabelValueIProps[]
  isOpen: boolean
}

export const CreateFacilityForm = ({
  form,
  facilityTypes,
  isOpen,
}: CreateFacilityFormIProps) => {
  const { t } = useTranslation()
  const {
    errors,
    control,
    trigger,
    register,
    getValues,
    setValue,
    unregister,
    reset,
    watch,
  } = form
  const type = watch('type')?.value
  const { latitude, longitude } = watch(['latitude', 'longitude', 'address'])

  const [latLng, setLatLng] = useState<LatLngStateIProps | null>(null)
  const [shouldShowMap, setShouldShowMap] = useState(false)

  useEffect(() => {
    if (type === FacilityType.Market) {
      handleMarketForm()
    } else {
      register('address', { required: true })
      register('latitude', { required: true })
      register('longitude', { required: true })
    }
    trigger(['address'])
  }, [type])

  const handleMarketForm = () => {
    reset({ ...getValues(), latitude: null, longitude: null, address: null })
    setShouldShowMap(false)
    unregister(['address', 'latitude', 'longitude'])
  }

  useEffect(() => {
    if (latRegex.test(latitude) && lonRegex.test(longitude)) {
      setLatLng({
        lat: parseFloat(latitude),
        lng: parseFloat(longitude),
      })
      setShouldShowMap(true)
    }
  }, [latitude, longitude, type])

  useEffect(() => {
    if (!isOpen) {
      reset()
      setShouldShowMap(false)
      setLatLng(null)
    }
  }, [isOpen])

  return (
    <form>
      <Box px='6'>
        <Box mb='3'>
          <FormInputControl
            errorMessage={errors.name && errors.name.message}
            id='name'
            inputRef={register({
              required: t('facilities:crud_facility_name_required'),
            })}
            isInvalid={!!errors.name}
            label={t('common:facility_name')}
            placeholder={t('facilities:crud_facility_name_placeholder')}
          />
        </Box>
        <Box mb='3'>
          <FormInputControl
            errorMessage={errors.shortName && errors.shortName.message}
            id='shortName'
            inputRef={register({
              required: t('facilities:crud_short_name_required'),
              maxLength: {
                value: 8,
                message: t('facilities:crud_short_name_max_length'),
              },
            })}
            isInvalid={!!errors.shortName}
            label={t('facilities:crud_short_name_label')}
            placeholder={t('facilities:crud_short_name_placeholder')}
          />
        </Box>
        <Box mb='3'>
          <FormInputSelectControl
            control={control}
            dataTestId='createFacility_facilityName'
            defaultValue={null}
            id='type'
            isInvalid={!!errors.type}
            label={t('facilities:crud_facility_type_label')}
            options={facilityTypes}
            placeholder={t('facilities:crud_facility_type_placeholder')}
            rules={{ required: true }}
          />
        </Box>

        <StyledFormInputControl mb='4'>
          <FormLabel>{t('common:address')}</FormLabel>
          <AddressSearch
            defaultValue={null}
            disabled={type === FacilityType.Market}
            onSelectAddress={(address) => {
              setValue('address', address?.value)
              if (address) {
                setValue('latitude', address?.latitude)
                setValue('longitude', address?.longitude)
                setLatLng({
                  lat: address?.latitude,
                  lng: address?.longitude,
                })
                setShouldShowMap(true)
                trigger(['address', 'latitude', 'longitude'])
              }
            }}
            showTooltip={false}
          />
          <FormErrorMessage>
            {errors.address && errors.address.message}
          </FormErrorMessage>
        </StyledFormInputControl>

        <HStack align='stretch' mb='4' spacing='3'>
          <FormInputControl
            disabled={type === FacilityType.Market}
            errorMessage={errors.latitude && errors.latitude.message}
            id='latitude'
            inputRef={register({
              validate: (v) => {
                if (type === FacilityType.Market) return true
                if (!v) return t('facilities:crud_latitude_required')
                if (!latRegex.test(v))
                  return t('facilities:crud_latitude_invalid')
                return true
              },
            })}
            isInvalid={Boolean(errors.latitude)}
            label={t('facilities:crud_latitude_label')}
            placeholder={t('facilities:crud_latitude_placeholder')}
          />
          <FormInputControl
            disabled={type === FacilityType.Market}
            errorMessage={errors.longitude && errors.longitude.message}
            id='longitude'
            inputRef={register({
              validate: (v) => {
                if (type === FacilityType.Market) return true
                if (!v) return t('facilities:crud_longitude_required')
                if (!lonRegex.test(v))
                  return t('facilities:crud_longitude_invalid')
                return true
              },
            })}
            isInvalid={Boolean(errors.longitude)}
            label={t('facilities:crud_longitude_label')}
            placeholder={t('facilities:crud_longitude_placeholder')}
          />
        </HStack>
      </Box>

      {shouldShowMap && (
        <StaticMapBox
          height='150px'
          latitude={latLng?.lat}
          longitude={latLng?.lng}
        >
          <PinMarker
            Icon={HiOutlineOfficeBuilding}
            bgGradient='linear(to-r, #1CFDE5, #07DB94)'
            borderColor='#42CD70'
            fadedPulseBoxShadow='rgba(43, 210, 180, 0)'
            pulseBoxShadow='rgba(43, 210, 180, 0.4)'
          />
        </StaticMapBox>
      )}
    </form>
  )
}
